import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { setToken, clearToken } from '../services/api';

interface PaymentStatus {
  status: string;
  due_date: string | null;
  days_overdue: number;
  is_overdue: boolean;
  is_blocked: boolean;
  trial_expired?: boolean;
}

interface User {
  id: number; name: string; email: string; role: string; staff_role?: string | null;
  company_id: number; company_slug?: string;
  company?: { name: string; logo_url?: string | null };
  payment_status?: PaymentStatus;
}

interface AuthCtx {
  user: User | null; loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUserSlug: (slug: string) => void;
}

const AuthContext = createContext<AuthCtx>({} as AuthCtx);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem('token');
    if (t) {
      setToken(t);
      api.get('/auth/me').then(({ data }) => setUser(data)).catch(() => clearToken()).finally(() => setLoading(false));
    } else setLoading(false);
  }, []);

  // Revalida o usuário (inclui payment_status) quando a aba/app volta a ficar
  // visível — evita que uma sessão de PWA deixada aberta em segundo plano
  // continue exibindo trial_expired/is_blocked desatualizado depois que um
  // pagamento é aprovado ou a conta é reativada no meio tempo.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('token')) return;
      api.get('/auth/me').then(({ data }) => setUser(data)).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    const { data } = await api.post('/auth/login', { email, password });
    setToken(data.token);
    const { data: me } = await api.get('/auth/me');
    setUser(me);
    // Login explícito reseta a soneca de avisos (ex.: WhatsAppStatusAlert) —
    // se o problema ainda existir, o dono volta a ver o aviso completo agora.
    localStorage.removeItem(`wa_alert_dismissed_until_${me.company_id}`);
    return me;
  };

  const logout = () => {
    api.post('/auth/logout');
    clearToken();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (_) {}
  };

  const updateUserSlug = (slug: string) => {
    setUser(prev => prev ? { ...prev, company_slug: slug } : prev);
  };

  return <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, updateUserSlug }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
